import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    getAvailability,
    createAvailabilityBlock,
    deleteAvailabilityBlock,
    getUnavailability,
    createUnavailabilityBlock,
    deleteUnavailabilityBlock,
    updateUnavailabilityBlock,
    getReservationSettings,
    updateReservationSetting,
    getRecurringUnavailability,
    createRecurringUnavailability,
    updateRecurringUnavailability,
    deleteRecurringUnavailability,
    getExternalCalendars,
    createExternalCalendar,
    updateExternalCalendar,
    deleteExternalCalendar,
    testExternalCalendar,
    verifyExternalCalendar
} from '../services/api';

function AvailabilityManagement({ onLogout }) {
    const [availability, setAvailability] = useState([]);
    const [unavailability, setUnavailability] = useState([]);
    const [recurringUnavailability, setRecurringUnavailability] = useState([]);
    const [reservationSettings, setReservationSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Existujúce stavy
    const [showAddModal, setShowAddModal] = useState(false);
    const [showUnavailabilityModal, setShowUnavailabilityModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [selectedDay, setSelectedDay] = useState('Pondelok');
    const [newBlock, setNewBlock] = useState({ start_time: '', end_time: '' });
    const [submitting, setSubmitting] = useState(false);
    const [lectorEmail, setLectorEmail] = useState('');

    // Stavy pre opakovanú nedostupnosť
    const [showRecurringModal, setShowRecurringModal] = useState(false);
    const [editingRecurring, setEditingRecurring] = useState(null);
    const [recurringData, setRecurringData] = useState({
        day_of_week: 'Pondelok',
        start_time: '',
        end_time: '',
        recurrence_type: 'weekly',
        end_date: { day: '', month: '', year: '' },
        start_from_date: { day: '', month: '', year: '' }
    });

    // Stavy pre jednorazovú nedostupnosť
    const [unavailabilityData, setUnavailabilityData] = useState({
        startDate: { day: '', month: '', year: '', time: '' },
        endDate: { day: '', month: '', year: '', time: '' }
    });
    const [editingUnavailability, setEditingUnavailability] = useState(null);
    const [editingSetting, setEditingSetting] = useState(null);
    const [newSettingValue, setNewSettingValue] = useState('');

    // Externé kalendáre
    const [externalCalendars, setExternalCalendars] = useState([]);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [newCalendarEmail, setNewCalendarEmail] = useState('');
    const [calendarTestResult, setCalendarTestResult] = useState(null);
    const [testingCalendar, setTestingCalendar] = useState(false);
    const [showCalendarHelp, setShowCalendarHelp] = useState(false);

    // Náhľad termínov
    const [previewSlots, setPreviewSlots] = useState([]);
    const [previewWeekOffset, setPreviewWeekOffset] = useState(0);
    const [loadingPreview, setLoadingPreview] = useState(false);

    // Chybové hlášky pre validáciu
    const [validationErrors, setValidationErrors] = useState({});

    const daysOfWeek = ["Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota", "Nedeľa"];

    const recurrenceTypes = [
        { value: 'weekly', label: 'Každý týždeň' },
        { value: 'biweekly', label: 'Každý druhý týždeň' },
        { value: 'monthly', label: 'Každý mesiac (rovnaký týždeň)' }
    ];

    // Generovanie hexa kódu z emailu pre rezervačný odkaz
    const getLectorHexId = () => {
        if (!lectorEmail) return '';
        const localPart = lectorEmail.split('@')[0];
        // Konverzia na hex
        let hex = '';
        for (let i = 0; i < localPart.length; i++) {
            hex += localPart.charCodeAt(i).toString(16).padStart(2, '0');
        }
        return hex;
    };

    const getReservationUrl = () => {
        const hexId = getLectorHexId();
        if (!hexId) return '#';
        return `https://rezervacia.douckobradzu.sk/#/${hexId}`;
    };

    // ==================== POMOCNÉ FUNKCIE PRE VALIDÁCIU DÁTUMOV ====================

    /**
     * Kontrola či je rok prestupný
     */
    const isLeapYear = (year) => {
        const y = parseInt(year);
        return (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
    };

    /**
     * Získanie maximálneho počtu dní v mesiaci
     */
    const getDaysInMonth = (month, year) => {
        const m = parseInt(month);
        const y = parseInt(year) || new Date().getFullYear();

        if (!m || m < 1 || m > 12) return 31;

        const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        if (m === 2 && isLeapYear(y)) {
            return 29;
        }

        return daysInMonths[m - 1];
    };

    /**
     * Korekcia dňa podľa mesiaca a roku
     */
    const correctDayForMonth = (day, month, year) => {
        if (!day || !month) return day;

        const maxDays = getDaysInMonth(month, year);
        const dayNum = parseInt(day);

        if (dayNum > maxDays) {
            return maxDays.toString().padStart(2, '0');
        }

        return day;
    };

    /**
     * Konverzia dátumových častí na Date objekt
     */
    const partsToDate = (parts, includeTime = false) => {
        if (!parts.day || !parts.month || !parts.year) return null;

        const dateStr = `${parts.year}-${parts.month}-${parts.day}`;

        if (includeTime && parts.time) {
            return new Date(`${dateStr}T${parts.time}`);
        }

        return new Date(dateStr);
    };

    /**
     * Validácia rozsahu dátumov (od musí byť pred do)
     */
    const validateDateRange = (startParts, endParts, includeTime = false) => {
        const startDate = partsToDate(startParts, includeTime);
        const endDate = partsToDate(endParts, includeTime);

        if (!startDate || !endDate) return true; // Ak nie sú kompletné, necháme prejsť

        return startDate < endDate;
    };

    /**
     * Generovanie dní s ohľadom na mesiac
     */
    const generateDaysForMonth = (month, year) => {
        const maxDays = getDaysInMonth(month, year);
        const days = [];
        for (let i = 1; i <= maxDays; i++) {
            days.push(i.toString().padStart(2, '0'));
        }
        return days;
    };

    const generateDays = () => {
        const days = [];
        for (let i = 1; i <= 31; i++) {
            days.push(i.toString().padStart(2, '0'));
        }
        return days;
    };

    const generateMonths = () => {
        const months = [];
        for (let i = 1; i <= 12; i++) {
            months.push(i.toString().padStart(2, '0'));
        }
        return months;
    };

    const generateYears = () => {
        const years = [];
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i <= currentYear + 5; i++) {
            years.push(i.toString());
        }
        return years;
    };

    // ==================== HANDLERY PRE JEDNORAZOVÚ NEDOSTUPNOSŤ ====================

    const updateStartDate = useCallback((field, value) => {
        setUnavailabilityData(prev => {
            const newStartDate = { ...prev.startDate, [field]: value };

            // Ak sa mení mesiac alebo rok, skontroluj a oprav deň
            if (field === 'month' || field === 'year') {
                newStartDate.day = correctDayForMonth(
                    newStartDate.day,
                    field === 'month' ? value : newStartDate.month,
                    field === 'year' ? value : newStartDate.year
                );
            }

            const newData = { ...prev, startDate: newStartDate };

            // Validácia rozsahu
            if (!validateDateRange(newStartDate, prev.endDate, true)) {
                setValidationErrors(e => ({ ...e, unavailabilityRange: 'Dátum začiatku musí byť pred dátumom konca' }));
            } else {
                setValidationErrors(e => ({ ...e, unavailabilityRange: null }));
            }

            return newData;
        });
    }, []);

    const updateEndDate = useCallback((field, value) => {
        setUnavailabilityData(prev => {
            const newEndDate = { ...prev.endDate, [field]: value };

            // Ak sa mení mesiac alebo rok, skontroluj a oprav deň
            if (field === 'month' || field === 'year') {
                newEndDate.day = correctDayForMonth(
                    newEndDate.day,
                    field === 'month' ? value : newEndDate.month,
                    field === 'year' ? value : newEndDate.year
                );
            }

            const newData = { ...prev, endDate: newEndDate };

            // Validácia rozsahu
            if (!validateDateRange(prev.startDate, newEndDate, true)) {
                setValidationErrors(e => ({ ...e, unavailabilityRange: 'Dátum začiatku musí byť pred dátumom konca' }));
            } else {
                setValidationErrors(e => ({ ...e, unavailabilityRange: null }));
            }

            return newData;
        });
    }, []);

    // ==================== HANDLERY PRE OPAKOVANÚ NEDOSTUPNOSŤ ====================

    const updateRecurringStartFromDate = useCallback((field, value) => {
        setRecurringData(prev => {
            const newStartFromDate = { ...prev.start_from_date, [field]: value };

            // Ak sa mení mesiac alebo rok, skontroluj a oprav deň
            if (field === 'month' || field === 'year') {
                newStartFromDate.day = correctDayForMonth(
                    newStartFromDate.day,
                    field === 'month' ? value : newStartFromDate.month,
                    field === 'year' ? value : newStartFromDate.year
                );
            }

            const newData = { ...prev, start_from_date: newStartFromDate };

            // Validácia rozsahu (len ak je vyplnený start_from_date)
            const hasStartFrom = newStartFromDate.day && newStartFromDate.month && newStartFromDate.year;
            const hasEndDate = prev.end_date.day && prev.end_date.month && prev.end_date.year;

            if (hasStartFrom && hasEndDate && !validateDateRange(newStartFromDate, prev.end_date, false)) {
                setValidationErrors(e => ({ ...e, recurringRange: 'Dátum "od" musí byť pred dátumom "do"' }));
            } else {
                setValidationErrors(e => ({ ...e, recurringRange: null }));
            }

            return newData;
        });
    }, []);

    const updateRecurringEndDate = useCallback((field, value) => {
        setRecurringData(prev => {
            const newEndDate = { ...prev.end_date, [field]: value };

            // Ak sa mení mesiac alebo rok, skontroluj a oprav deň
            if (field === 'month' || field === 'year') {
                newEndDate.day = correctDayForMonth(
                    newEndDate.day,
                    field === 'month' ? value : newEndDate.month,
                    field === 'year' ? value : newEndDate.year
                );
            }

            const newData = { ...prev, end_date: newEndDate };

            // Validácia rozsahu (len ak je vyplnený start_from_date)
            const hasStartFrom = prev.start_from_date.day && prev.start_from_date.month && prev.start_from_date.year;
            const hasEndDate = newEndDate.day && newEndDate.month && newEndDate.year;

            if (hasStartFrom && hasEndDate && !validateDateRange(prev.start_from_date, newEndDate, false)) {
                setValidationErrors(e => ({ ...e, recurringRange: 'Dátum "od" musí byť pred dátumom "do"' }));
            } else {
                setValidationErrors(e => ({ ...e, recurringRange: null }));
            }

            return newData;
        });
    }, []);

    // ==================== LOAD DATA ====================

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [availabilityData, unavailabilityData, recurringData, settingsData, calendarsData] = await Promise.all([
                getAvailability(),
                getUnavailability(),
                getRecurringUnavailability(),
                getReservationSettings(),
                getExternalCalendars()
            ]);

            setAvailability(availabilityData);
            setUnavailability(unavailabilityData);
            setRecurringUnavailability(recurringData);
            setReservationSettings(settingsData);
            setExternalCalendars(calendarsData);

            // Získame email lektora zo sessionStorage
            const email = sessionStorage.getItem('lectorEmail') || '';
            setLectorEmail(email);
        } catch (err) {
            setError('Nepodarilo sa načítať dáta: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // ==================== HANDLERY PRE DOSTUPNOSŤ ====================

    const handleAddBlock = async () => {
        if (!newBlock.start_time || !newBlock.end_time) {
            alert('Vyplňte čas začiatku a konca');
            return;
        }

        if (newBlock.start_time >= newBlock.end_time) {
            alert('Čas začiatku musí byť pred časom konca');
            return;
        }

        setSubmitting(true);
        try {
            const createdBlock = await createAvailabilityBlock({
                day_of_week: selectedDay,
                start_time: newBlock.start_time,
                end_time: newBlock.end_time
            });

            setAvailability(prev => [...prev, createdBlock]);
            setShowAddModal(false);
            setNewBlock({ start_time: '', end_time: '' });
        } catch (err) {
            alert('Chyba pri pridávaní bloku: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteBlock = async (blockId) => {
        if (!window.confirm('Naozaj chcete odstrániť tento blok?')) {
            return;
        }

        try {
            await deleteAvailabilityBlock(blockId);
            setAvailability(prev => prev.filter(block => block.id !== blockId));
        } catch (err) {
            alert('Chyba pri odstraňovaní bloku: ' + err.message);
        }
    };

    // ==================== HANDLERY PRE JEDNORAZOVÚ NEDOSTUPNOSŤ ====================

    const handleSaveUnavailabilityBlock = async () => {
        const { startDate, endDate } = unavailabilityData;

        if (!startDate.day || !startDate.month || !startDate.year || !startDate.time ||
            !endDate.day || !endDate.month || !endDate.year || !endDate.time) {
            alert('Vyplňte všetky polia pre začiatok a koniec');
            return;
        }

        // Finálna validácia rozsahu
        if (!validateDateRange(startDate, endDate, true)) {
            alert('Dátum a čas začiatku musí byť pred dátumom a časom konca');
            return;
        }

        const startDateTime = `${startDate.day}.${startDate.month}.${startDate.year} ${startDate.time}`;
        const endDateTime = `${endDate.day}.${endDate.month}.${endDate.year} ${endDate.time}`;

        setSubmitting(true);
        try {
            if (editingUnavailability) {
                const updatedBlock = await updateUnavailabilityBlock(editingUnavailability.id, {
                    start_time: startDateTime,
                    end_time: endDateTime
                });
                setUnavailability(prev => prev.map(block =>
                    block.id === editingUnavailability.id ? updatedBlock : block
                ));
            } else {
                const newBlock = await createUnavailabilityBlock({
                    start_time: startDateTime,
                    end_time: endDateTime
                });
                setUnavailability(prev => [...prev, newBlock]);
            }

            handleCloseUnavailabilityModal();
        } catch (err) {
            alert('Chyba pri ukladaní nedostupnosti: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteUnavailabilityBlock = async (blockId) => {
        if (!window.confirm('Naozaj chcete odstrániť tento blok nedostupnosti?')) {
            return;
        }

        try {
            await deleteUnavailabilityBlock(blockId);
            setUnavailability(prev => prev.filter(block => block.id !== blockId));
        } catch (err) {
            alert('Chyba pri odstraňovaní bloku: ' + err.message);
        }
    };

    const handleEditUnavailability = (block) => {
        const [startDateStr, startTimeStr] = block.start_time.split(' ');
        const [startDay, startMonth, startYear] = startDateStr.split('.');

        const [endDateStr, endTimeStr] = block.end_time.split(' ');
        const [endDay, endMonth, endYear] = endDateStr.split('.');

        setUnavailabilityData({
            startDate: {
                day: startDay,
                month: startMonth,
                year: startYear,
                time: startTimeStr
            },
            endDate: {
                day: endDay,
                month: endMonth,
                year: endYear,
                time: endTimeStr
            }
        });
        setEditingUnavailability(block);
        setValidationErrors({});
        setShowUnavailabilityModal(true);
    };

    const handleCloseUnavailabilityModal = () => {
        setShowUnavailabilityModal(false);
        setUnavailabilityData({
            startDate: { day: '', month: '', year: '', time: '' },
            endDate: { day: '', month: '', year: '', time: '' }
        });
        setEditingUnavailability(null);
        setValidationErrors({});
    };

    // ==================== HANDLERY PRE OPAKOVANÚ NEDOSTUPNOSŤ ====================

    const handleSaveRecurringUnavailability = async () => {
        if (!recurringData.start_time || !recurringData.end_time) {
            alert('Vyplňte čas začiatku a konca');
            return;
        }

        if (recurringData.start_time >= recurringData.end_time) {
            alert('Čas začiatku musí byť pred časom konca');
            return;
        }

        if (!recurringData.end_date.day || !recurringData.end_date.month || !recurringData.end_date.year) {
            alert('Vyplňte dátum platnosti do');
            return;
        }

        // Validácia rozsahu ak je vyplnený start_from_date
        const hasStartFrom = recurringData.start_from_date.day &&
            recurringData.start_from_date.month &&
            recurringData.start_from_date.year;

        if (hasStartFrom && !validateDateRange(recurringData.start_from_date, recurringData.end_date, false)) {
            alert('Dátum "od" musí byť pred dátumom "do"');
            return;
        }

        const endDateStr = `${recurringData.end_date.day}.${recurringData.end_date.month}.${recurringData.end_date.year}`;

        let startFromDateStr = null;
        if (hasStartFrom) {
            startFromDateStr = `${recurringData.start_from_date.day}.${recurringData.start_from_date.month}.${recurringData.start_from_date.year}`;
        }

        const payload = {
            day_of_week: recurringData.day_of_week,
            start_time: recurringData.start_time,
            end_time: recurringData.end_time,
            recurrence_type: recurringData.recurrence_type,
            end_date: endDateStr,
            start_from_date: startFromDateStr
        };

        setSubmitting(true);
        try {
            if (editingRecurring) {
                const updatedBlock = await updateRecurringUnavailability(editingRecurring.id, payload);
                setRecurringUnavailability(prev => prev.map(block =>
                    block.id === editingRecurring.id ? updatedBlock : block
                ));
            } else {
                const newBlock = await createRecurringUnavailability(payload);
                setRecurringUnavailability(prev => [...prev, newBlock]);
            }

            handleCloseRecurringModal();
        } catch (err) {
            alert('Chyba pri ukladaní opakovanej nedostupnosti: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteRecurringUnavailability = async (blockId) => {
        if (!window.confirm('Naozaj chcete odstrániť túto opakovanú nedostupnosť?')) {
            return;
        }

        try {
            await deleteRecurringUnavailability(blockId);
            setRecurringUnavailability(prev => prev.filter(block => block.id !== blockId));
        } catch (err) {
            alert('Chyba pri odstraňovaní: ' + err.message);
        }
    };

    const handleEditRecurring = (block) => {
        let endDateParts = { day: '', month: '', year: '' };
        let startFromDateParts = { day: '', month: '', year: '' };

        if (block.end_date) {
            const parts = block.end_date.split('.');
            if (parts.length === 3) {
                endDateParts = { day: parts[0], month: parts[1], year: parts[2] };
            }
        }

        if (block.start_from_date) {
            const parts = block.start_from_date.split('.');
            if (parts.length === 3) {
                startFromDateParts = { day: parts[0], month: parts[1], year: parts[2] };
            }
        }

        setRecurringData({
            day_of_week: block.day_of_week,
            start_time: block.start_time,
            end_time: block.end_time,
            recurrence_type: block.recurrence_type,
            end_date: endDateParts,
            start_from_date: startFromDateParts
        });
        setEditingRecurring(block);
        setValidationErrors({});
        setShowRecurringModal(true);
    };

    const handleCloseRecurringModal = () => {
        setShowRecurringModal(false);
        setEditingRecurring(null);
        setRecurringData({
            day_of_week: 'Pondelok',
            start_time: '',
            end_time: '',
            recurrence_type: 'weekly',
            end_date: { day: '', month: '', year: '' },
            start_from_date: { day: '', month: '', year: '' }
        });
        setValidationErrors({});
    };

    // ==================== HANDLERY PRE NASTAVENIA ====================

    const handleSaveSetting = async () => {
        if (!newSettingValue) {
            alert('Zadajte hodnotu');
            return;
        }

        setSubmitting(true);
        try {
            const updatedSetting = await updateReservationSetting(editingSetting.id, { cas: newSettingValue });
            setReservationSettings(prev => prev.map(setting =>
                setting.id === editingSetting.id ? updatedSetting : setting
            ));
            setShowSettingsModal(false);
            setEditingSetting(null);
            setNewSettingValue('');
        } catch (err) {
            alert('Chyba pri ukladaní nastavenia: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSetting = (setting) => {
        setEditingSetting(setting);
        setNewSettingValue(setting.cas);
        setShowSettingsModal(true);
    };

    const handleCloseSettingsModal = () => {
        setShowSettingsModal(false);
        setEditingSetting(null);
        setNewSettingValue('');
    };

    // ==================== HANDLERY PRE EXTERNÉ KALENDÁRE ====================

    const handleTestCalendar = async () => {
        if (!newCalendarEmail) {
            alert('Zadajte email kalendára');
            return;
        }

        setTestingCalendar(true);
        setCalendarTestResult(null);

        try {
            const result = await testExternalCalendar(newCalendarEmail);
            setCalendarTestResult(result);
        } catch (err) {
            setCalendarTestResult({
                success: false,
                message: err.message || 'Chyba pri testovaní kalendára'
            });
        } finally {
            setTestingCalendar(false);
        }
    };

    const handleAddCalendar = async () => {
        if (!newCalendarEmail) {
            alert('Zadajte email kalendára');
            return;
        }

        if (!calendarTestResult?.success) {
            alert('Najprv otestujte pripojenie ku kalendáru');
            return;
        }

        setSubmitting(true);
        try {
            const newCalendar = await createExternalCalendar(newCalendarEmail);

            // Označiť ako overený, keďže test prebehol úspešne
            await updateExternalCalendar(newCalendar.id, { is_verified: true });
            newCalendar.is_verified = true;

            setExternalCalendars(prev => [...prev, newCalendar]);
            handleCloseCalendarModal();
        } catch (err) {
            alert('Chyba pri pridávaní kalendára: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleCalendarActive = async (calendarId, currentActive) => {
        try {
            const updated = await updateExternalCalendar(calendarId, { is_active: !currentActive });
            setExternalCalendars(prev => prev.map(cal =>
                cal.id === calendarId ? { ...cal, is_active: !currentActive } : cal
            ));
        } catch (err) {
            alert('Chyba pri aktualizácii kalendára: ' + err.message);
        }
    };

    const handleDeleteCalendar = async (calendarId) => {
        if (!window.confirm('Naozaj chcete odstrániť tento kalendár?')) {
            return;
        }

        try {
            await deleteExternalCalendar(calendarId);
            setExternalCalendars(prev => prev.filter(cal => cal.id !== calendarId));
        } catch (err) {
            alert('Chyba pri odstraňovaní kalendára: ' + err.message);
        }
    };

    const handleVerifyCalendar = async (calendarId) => {
        try {
            await verifyExternalCalendar(calendarId);
            setExternalCalendars(prev => prev.map(cal =>
                cal.id === calendarId ? { ...cal, is_verified: true } : cal
            ));
        } catch (err) {
            alert('Overenie zlyhalo: ' + err.message);
            setExternalCalendars(prev => prev.map(cal =>
                cal.id === calendarId ? { ...cal, is_verified: false } : cal
            ));
        }
    };

    const handleCloseCalendarModal = () => {
        setShowCalendarModal(false);
        setNewCalendarEmail('');
        setCalendarTestResult(null);
        setShowCalendarHelp(false);
    };

    const SERVICE_ACCOUNT_EMAIL = 'douckobradzu@douckobradzu.iam.gserviceaccount.com';

    // ==================== NÁHĽAD TERMÍNOV ====================

    const calculatePreviewSlots = useCallback(() => {
        setLoadingPreview(true);

        try {
            // Získame nastavenia
            let minHours = 24;
            let maxDays = 60;
            let gapMinutes = 15;

            reservationSettings.forEach(setting => {
                if (setting.typ === 'Najskôr') minHours = parseInt(setting.cas) || 24;
                if (setting.typ === 'Najneskôr') maxDays = parseInt(setting.cas) || 60;
                if (setting.typ === 'Medzera') gapMinutes = parseInt(setting.cas) || 15;
            });

            const slotDuration = 45; // minút
            const slotWithGap = slotDuration + gapMinutes;

            const now = new Date();
            const startDate = new Date(now.getTime() + minHours * 60 * 60 * 1000);
            const endDate = new Date(now.getTime() + maxDays * 24 * 60 * 60 * 1000);

            // Rozbalenie opakovanej nedostupnosti
            const expandedUnavailability = expandRecurringUnavailability(
                recurringUnavailability,
                startDate,
                endDate
            );

            // Kombinovaná nedostupnosť
            const allUnavailability = [...unavailability, ...expandedUnavailability];

            // Generovanie slotov
            const slots = [];
            const currentDay = new Date(startDate);
            currentDay.setHours(0, 0, 0, 0);

            while (currentDay <= endDate) {
                const dayName = getDayNameFromDate(currentDay);
                const dayAvailability = availability.filter(a => a.day_of_week === dayName);

                dayAvailability.forEach(avail => {
                    const [startHour, startMin] = avail.start_time.split(':').map(Number);
                    const [endHour, endMin] = avail.end_time.split(':').map(Number);

                    const blockStart = new Date(currentDay);
                    blockStart.setHours(startHour, startMin, 0, 0);

                    const blockEnd = new Date(currentDay);
                    blockEnd.setHours(endHour, endMin, 0, 0);

                    let slotStart = new Date(blockStart);

                    while (slotStart.getTime() + slotWithGap * 60 * 1000 <= blockEnd.getTime()) {
                        const slotEnd = new Date(slotStart.getTime() + slotDuration * 60 * 1000);

                        // Preskočíme ak je slot pred minimálnym časom
                        if (slotStart >= startDate) {
                            // Kontrola nedostupnosti
                            if (!isSlotUnavailable(slotStart, slotEnd, allUnavailability)) {
                                slots.push({
                                    start: new Date(slotStart),
                                    end: new Date(slotEnd)
                                });
                            }
                        }

                        slotStart = new Date(slotStart.getTime() + slotWithGap * 60 * 1000);
                    }
                });

                currentDay.setDate(currentDay.getDate() + 1);
            }

            setPreviewSlots(slots);
        } catch (err) {
            console.error('Error calculating preview slots:', err);
            setPreviewSlots([]);
        } finally {
            setLoadingPreview(false);
        }
    }, [availability, unavailability, recurringUnavailability, reservationSettings]);

    // Helper funkcie pre náhľad
    const getDayNameFromDate = (date) => {
        const days = ["Nedeľa", "Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota"];
        return days[date.getDay()];
    };

    const expandRecurringUnavailability = (recurring, startDate, endDate) => {
        const expanded = [];

        if (!recurring || recurring.length === 0) return expanded;

        const dayNameToIndex = {
            "Pondelok": 1, "Utorok": 2, "Streda": 3, "Štvrtok": 4,
            "Piatok": 5, "Sobota": 6, "Nedeľa": 0
        };

        recurring.forEach(block => {
            const dayIndex = dayNameToIndex[block.day_of_week];
            if (dayIndex === undefined) return;

            // Parsovanie dátumov platnosti
            let blockEndDate = endDate;
            if (block.end_date) {
                const [day, month, year] = block.end_date.split('.');
                blockEndDate = new Date(year, month - 1, day, 23, 59, 59);
            }

            let blockStartDate = startDate;
            if (block.start_from_date) {
                const [day, month, year] = block.start_from_date.split('.');
                blockStartDate = new Date(year, month - 1, day, 0, 0, 0);
            }

            // Nájdeme prvý deň
            let current = new Date(Math.max(startDate.getTime(), blockStartDate.getTime()));
            while (current.getDay() !== dayIndex) {
                current.setDate(current.getDate() + 1);
            }

            let weekCounter = 0;

            while (current <= Math.min(blockEndDate, endDate)) {
                let shouldAdd = false;

                if (block.recurrence_type === 'weekly') {
                    shouldAdd = true;
                } else if (block.recurrence_type === 'biweekly') {
                    shouldAdd = (weekCounter % 2 === 0);
                } else if (block.recurrence_type === 'monthly') {
                    const weekOfMonth = Math.floor((current.getDate() - 1) / 7);
                    const firstOccurrenceWeek = Math.floor((blockStartDate.getDate() - 1) / 7);
                    shouldAdd = (weekOfMonth === firstOccurrenceWeek);
                }

                if (shouldAdd && current >= blockStartDate) {
                    const [startHour, startMin] = block.start_time.split(':').map(Number);
                    const [endHour, endMin] = block.end_time.split(':').map(Number);

                    const unavailStart = new Date(current);
                    unavailStart.setHours(startHour, startMin, 0, 0);

                    const unavailEnd = new Date(current);
                    unavailEnd.setHours(endHour, endMin, 0, 0);

                    expanded.push({
                        start: unavailStart,
                        end: unavailEnd
                    });
                }

                current.setDate(current.getDate() + 7);
                weekCounter++;
            }
        });

        return expanded;
    };

    const isSlotUnavailable = (slotStart, slotEnd, unavailability) => {
        for (const unavail of unavailability) {
            let unavailStart, unavailEnd;

            if (unavail.start instanceof Date) {
                unavailStart = unavail.start;
                unavailEnd = unavail.end;
            } else {
                // Parsovanie z DD.MM.YYYY HH:MM formátu
                try {
                    const [startDateStr, startTimeStr] = unavail.start_time.split(' ');
                    const [startDay, startMonth, startYear] = startDateStr.split('.');
                    const [startHour, startMin] = startTimeStr.split(':');
                    unavailStart = new Date(startYear, startMonth - 1, startDay, startHour, startMin);

                    const [endDateStr, endTimeStr] = unavail.end_time.split(' ');
                    const [endDay, endMonth, endYear] = endDateStr.split('.');
                    const [endHour, endMin] = endTimeStr.split(':');
                    unavailEnd = new Date(endYear, endMonth - 1, endDay, endHour, endMin);
                } catch (e) {
                    continue;
                }
            }

            // Kontrola prekrývania
            if (slotStart < unavailEnd && slotEnd > unavailStart) {
                return true;
            }
        }

        return false;
    };

    // Prepočítaj náhľad pri zmene dát
    useEffect(() => {
        if (!loading) {
            calculatePreviewSlots();
        }
    }, [availability, unavailability, recurringUnavailability, reservationSettings, calculatePreviewSlots, loading]);

    // Funkcie pre navigáciu v náhľade
    const getPreviewWeekDays = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (previewWeekOffset * 7));

        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            weekDays.push(day);
        }
        return weekDays;
    };

    const getSlotsForDay = (date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        return previewSlots.filter(slot =>
            slot.start >= dayStart && slot.start <= dayEnd
        ).sort((a, b) => a.start - b.start);
    };

    const formatPreviewTime = (date) => {
        return date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
    };

    const formatPreviewDate = (date) => {
        return date.toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric' });
    };

    const formatPreviewWeekRange = () => {
        const weekDays = getPreviewWeekDays();
        const start = weekDays[0];
        const end = weekDays[6];

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const weekStart = new Date(start);
        weekStart.setHours(0, 0, 0, 0);

        let prefix = "";
        const diffDays = Math.round((weekStart - today) / (24 * 60 * 60 * 1000));

        if (diffDays >= 0 && diffDays < 7) {
            prefix = "Tento týždeň • ";
        } else if (diffDays >= 7 && diffDays < 14) {
            prefix = "Budúci týždeň • ";
        } else if (diffDays >= 14 && diffDays < 21) {
            prefix = "O 2 týždne • ";
        }

        return `${prefix}${formatPreviewDate(start)} - ${formatPreviewDate(end)}`;
    };

    // ==================== POMOCNÉ FUNKCIE ====================

    const getBlocksForDay = (day) => {
        return availability.filter(block => block.day_of_week === day);
    };

    const getRecurrenceLabel = (type) => {
        const labels = {
            'weekly': 'Každý týždeň',
            'biweekly': 'Každý druhý týždeň',
            'monthly': 'Každý mesiac'
        };
        return labels[type] || type;
    };

    /**
     * Renderovanie select pre deň s dynamickým počtom dní
     */
    const renderDaySelect = (value, onChange, month, year, keyPrefix) => {
        const days = month ? generateDaysForMonth(month, year) : generateDays();

        return (
            <select
                value={value}
                onChange={(e) => onChange('day', e.target.value)}
                className="date-select"
            >
                <option value="">DD</option>
                {days.map(day => (
                    <option key={`${keyPrefix}-${day}`} value={day}>{day}</option>
                ))}
            </select>
        );
    };

    if (loading) return <div className="loading">Načítavam dáta...</div>;
    if (error) return <div className="error">{error}</div>;

    // ==================== POMOCNÉ FUNKCIE PRE NASTAVENIA ====================

    const getSettingTitle = (typ) => {
        const titles = {
            'Najskôr': 'Najskôr',
            'Najneskôr': 'Najneskôr',
            'Medzera': 'Medzera medzi lekciami'
        };
        return titles[typ] || typ;
    };

    const getSettingDescription = (typ) => {
        const descriptions = {
            'Najskôr': 'Minimálny počet hodín pred rezerváciou',
            'Najneskôr': 'Maximálny počet dní do budúcna pre rezerváciu',
            'Medzera': 'Prestávka medzi jednotlivými lekciami'
        };
        return descriptions[typ] || '';
    };

    const getSettingUnit = (typ) => {
        const units = {
            'Najskôr': 'hodín',
            'Najneskôr': 'dní',
            'Medzera': 'minút'
        };
        return units[typ] || '';
    };

    const getGapOptions = () => {
        const options = [];
        for (let i = 5; i <= 30; i += 5) {
            options.push(i);
        }
        return options;
    };

    return (
        <div className="availability-container">
            <header className="main-header">
                <div className="user-greeting">
                    <h1>Správa dostupnosti</h1>
                    <p>Nastavte si svoje časové bloky a obdobia nedostupnosti</p>
                </div>
                <div className="header-actions">
                    <Link to="/dashboard" className="back-btn">
                        Späť na dashboard
                    </Link>
                    <button className="logout-btn" onClick={onLogout}>
                        Odhlásiť sa
                    </button>
                </div>
            </header>

            {/* Dvojstĺpcové rozloženie */}
            <div className="availability-layout">
                {/* Ľavý stĺpec - Dostupnosť */}
                <section className="availability-column">
                    <div className="column-header">
                        <h2>Dostupnosť</h2>
                        <p>Pravidelná dostupnosť podľa dní v týždni</p>
                    </div>

                    <div className="days-list">
                        {daysOfWeek.map(day => (
                            <div key={day} className="day-item">
                                <h3>{day}</h3>
                                <div className="time-blocks">
                                    {getBlocksForDay(day).length === 0 ? (
                                        <div className="no-availability">Žiadna dostupnosť</div>
                                    ) : (
                                        getBlocksForDay(day).map(block => (
                                            <div key={block.id} className="time-block">
                                                <span className="time-range">
                                                    <span className="time-icon">🕒</span>
                                                    {block.start_time} - {block.end_time}
                                                </span>
                                                <button
                                                    onClick={() => handleDeleteBlock(block.id)}
                                                    className="delete-btn small"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setSelectedDay(day);
                                        setShowAddModal(true);
                                    }}
                                    className="add-time-btn"
                                >
                                    + Pridať čas
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Pravý stĺpec - Nedostupnosť */}
                <section className="unavailability-column">
                    <div className="column-header">
                        <h2>Nedostupnosť</h2>
                        <p>Obdobie kedy ste nedostupní</p>
                        <div className="unavailability-buttons">
                            <button
                                onClick={() => {
                                    setEditingUnavailability(null);
                                    setValidationErrors({});
                                    setShowUnavailabilityModal(true);
                                }}
                                className="add-unavailability-btn"
                            >
                                + Jednorazová
                            </button>
                            <button
                                onClick={() => {
                                    setEditingRecurring(null);
                                    setValidationErrors({});
                                    setShowRecurringModal(true);
                                }}
                                className="add-unavailability-btn recurring"
                            >
                                + Opakovaná
                            </button>
                        </div>
                    </div>

                    {/* Jednorazová nedostupnosť */}
                    <div className="unavailability-section">
                        <h3 className="subsection-title">Jednorazová nedostupnosť</h3>
                        <div className="unavailability-list">
                            {unavailability.length === 0 ? (
                                <div className="no-unavailability">
                                    <p>Žiadna jednorazová nedostupnosť</p>
                                </div>
                            ) : (
                                unavailability.map(block => (
                                    <div key={block.id} className="unavailability-item">
                                        <div className="unavailability-period">
                                            <span className="period-icon">🚫</span>
                                            <div className="period-dates">
                                                <span className="period-range">{block.start_time} - {block.end_time}</span>
                                            </div>
                                        </div>
                                        <div className="unavailability-actions">
                                            <button
                                                onClick={() => handleEditUnavailability(block)}
                                                className="edit-btn"
                                                title="Upraviť"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUnavailabilityBlock(block.id)}
                                                className="delete-btn"
                                                title="Odstrániť"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Opakovaná nedostupnosť */}
                    <div className="unavailability-section">
                        <h3 className="subsection-title">Opakovaná nedostupnosť</h3>
                        <div className="unavailability-list">
                            {recurringUnavailability.length === 0 ? (
                                <div className="no-unavailability">
                                    <p>Žiadna opakovaná nedostupnosť</p>
                                    <small>Napr. každý druhý štvrtok 16:00-19:00</small>
                                </div>
                            ) : (
                                recurringUnavailability.map(block => (
                                    <div key={block.id} className="unavailability-item recurring">
                                        <div className="unavailability-period">
                                            <span className="period-icon">🔄</span>
                                            <div className="period-dates">
                                                <span className="period-range">
                                                    {getRecurrenceLabel(block.recurrence_type)} - {block.day_of_week}
                                                </span>
                                                <span className="period-time">
                                                    {block.start_time} - {block.end_time}
                                                </span>
                                                <span className="period-validity">
                                                    {block.start_from_date ? `Od ${block.start_from_date} ` : ''}
                                                    do {block.end_date}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="unavailability-actions">
                                            <button
                                                onClick={() => handleEditRecurring(block)}
                                                className="edit-btn"
                                                title="Upraviť"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDeleteRecurringUnavailability(block.id)}
                                                className="delete-btn"
                                                title="Odstrániť"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </section>
            </div>

            {/* Sekcia pre nastavenia rezervácií */}
            <section className="reservation-settings-section">
                <div className="section-header">
                    <h2>Nastavenia rezervácií</h2>
                    <p>Konfigurujte si pravidlá pre rezervácie lekcií</p>
                </div>

                <div className="settings-grid">
                    {reservationSettings.map(setting => (
                        <div key={setting.id} className="setting-card">
                            <div className="setting-content">
                                <h3>{getSettingTitle(setting.typ)}</h3>
                                <p className="setting-description">
                                    {getSettingDescription(setting.typ)}
                                </p>
                                <div className="setting-value">
                                    <span className="value">{setting.cas}</span>
                                    <span className="unit">
                                        {getSettingUnit(setting.typ)}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => handleEditSetting(setting)}
                                className="edit-setting-btn"
                            >
                                Upraviť
                            </button>
                        </div>
                    ))}
                </div>
            </section>
            {/* Sekcia pre externé kalendáre */}
            <section className="external-calendars-section">
                <div className="section-header">
                    <h2>Prepojené kalendáre</h2>
                    <p>Pridaj ďalšie Google kalendáre, ktoré sa budú zohľadňovať pri výpočte voľných termínov</p>
                </div>

                <div className="calendar-disclaimer">
                    <span className="disclaimer-icon">ℹ️</span>
                    <span>Lekcie sa naďalej budú zapisovať do vášho firemného kalendára.</span>
                </div>

                <div className="calendars-list">
                    {externalCalendars.length === 0 ? (
                        <div className="no-calendars">
                            <p>Nemáte pridané žiadne externé kalendáre</p>
                            <small>Externé kalendáre sa používajú na kontrolu vašej dostupnosti</small>
                        </div>
                    ) : (
                        externalCalendars.map(calendar => (
                            <div key={calendar.id} className={`calendar-item ${!calendar.is_active ? 'inactive' : ''}`}>
                                <div className="calendar-info">
                                    <span className="calendar-icon">📅</span>
                                    <div className="calendar-details">
                                        <span className="calendar-email">{calendar.calendar_email}</span>
                                        <div className="calendar-badges">
                                            <span className={`badge ${calendar.is_active ? 'active' : 'inactive'}`}>
                                                {calendar.is_active ? '✓ Aktívny' : '○ Neaktívny'}
                                            </span>
                                            <span className={`badge ${calendar.is_verified ? 'verified' : 'unverified'}`}>
                                                {calendar.is_verified ? '✓ Overený' : '⚠ Neoverený'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="calendar-actions">
                                    <button
                                        onClick={() => handleToggleCalendarActive(calendar.id, calendar.is_active)}
                                        className={`toggle-btn ${calendar.is_active ? 'active' : ''}`}
                                        title={calendar.is_active ? 'Deaktivovať' : 'Aktivovať'}
                                    >
                                        {calendar.is_active ? '🔵' : '⚪'}
                                    </button>
                                    {!calendar.is_verified && (
                                        <button
                                            onClick={() => handleVerifyCalendar(calendar.id)}
                                            className="verify-btn"
                                            title="Overiť pripojenie"
                                        >
                                            🔄
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteCalendar(calendar.id)}
                                        className="delete-btn"
                                        title="Odstrániť"
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {externalCalendars.length < 3 && (
                    <button
                        onClick={() => setShowCalendarModal(true)}
                        className="add-calendar-btn"
                    >
                        + Pridať kalendár
                    </button>
                )}
            </section>
            {/* Sekcia náhľadu termínov */}
            <section className="preview-section">
                <div className="section-header">
                    <h2>Náhľad voľných termínov</h2>
                    <p>Termíny vygenerované na základe tvojich nastavení bez ohľadu na existujúce rezervácie</p>
                    <p className="preview-link-text">
                        Pre náhľad skutočných voľných termínov s ohľadom na existujúce rezervácie{' '}
                        <a
                            href={getReservationUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="preview-reservation-link"
                        >
                            klikni sem
                        </a> (toto sa zobrazí tvojim študentom).
                    </p>
                </div>

                <div className="preview-calendar">
                    <div className="preview-header">
                        <button
                            onClick={() => setPreviewWeekOffset(prev => Math.max(0, prev - 1))}
                            className="week-nav-button"
                            disabled={previewWeekOffset <= 0}
                        >
                            ← Predchádzajúci
                        </button>
                        <h4>{formatPreviewWeekRange()}</h4>
                        <button
                            onClick={() => setPreviewWeekOffset(prev => prev + 1)}
                            className="week-nav-button"
                        >
                            Ďalší →
                        </button>
                    </div>

                    {loadingPreview ? (
                        <div className="preview-loading">
                            <div className="loading-spinner"></div>
                            <span>Počítam termíny...</span>
                        </div>
                    ) : (
                        <div className="preview-week-grid">
                            {getPreviewWeekDays().map((day, index) => {
                                const daySlots = getSlotsForDay(day);
                                const dayName = getDayNameFromDate(day);
                                const isToday = day.toDateString() === new Date().toDateString();

                                return (
                                    <div key={index} className={`preview-day-column ${isToday ? 'today' : ''}`}>
                                        <div className="preview-day-header">
                                            <div className="preview-day-name">{dayName}</div>
                                            <div className="preview-day-date">{formatPreviewDate(day)}</div>
                                        </div>
                                        <div className="preview-slots-container">
                                            {daySlots.length === 0 ? (
                                                <div className="preview-no-slots">Žiadne termíny</div>
                                            ) : (
                                                daySlots.map((slot, slotIndex) => (
                                                    <div key={slotIndex} className="preview-slot">
                                                        {formatPreviewTime(slot.start)}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="preview-summary">
                        <span className="preview-count">
                            📊 Celkovo {previewSlots.length} voľných termínov v nasledujúcich {
                                reservationSettings.find(s => s.typ === 'Najneskôr')?.cas || 60
                            } dňoch
                        </span>
                    </div>
                </div>
            </section>

            {/* Modal pre pridanie kalendára */}
            {showCalendarModal && (
                <div className="modal-overlay">
                    <div className="modal calendar-modal">
                        <div className="modal-header-row">
                            <h3>Pridať kalendár</h3>
                            <button
                                className="close-modal-btn"
                                onClick={handleCloseCalendarModal}
                                disabled={submitting}
                            >
                                ×
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Email kalendára:</label>
                            <input
                                type="email"
                                value={newCalendarEmail}
                                onChange={(e) => {
                                    setNewCalendarEmail(e.target.value);
                                    setCalendarTestResult(null);
                                }}
                                placeholder="kalendar@gmail.com"
                                disabled={submitting}
                            />
                            <small className="setting-help-text">
                                Zadajte email vášho Google kalendára
                            </small>
                        </div>

                        {/* Výsledok testu */}
                        {calendarTestResult && (
                            <div className={`test-result ${calendarTestResult.success ? 'success' : 'error'}`}>
                                <span className="test-icon">
                                    {calendarTestResult.success ? '✅' : '❌'}
                                </span>
                                <div className="test-message">
                                    <strong>{calendarTestResult.success ? 'Úspešne pripojené!' : 'Pripojenie zlyhalo'}</strong>
                                    <p>{calendarTestResult.message}</p>
                                    {calendarTestResult.calendar_name && (
                                        <p className="test-detail">Názov: {calendarTestResult.calendar_name}</p>
                                    )}
                                    {calendarTestResult.events_count !== null && calendarTestResult.events_count !== undefined && (
                                        <p className="test-detail">Udalostí v najbližších 7 dňoch: {calendarTestResult.events_count}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Návod */}
                        <div className="calendar-help-section">
                            <button
                                className="help-toggle-btn"
                                onClick={() => setShowCalendarHelp(!showCalendarHelp)}
                            >
                                {showCalendarHelp ? '▼' : '▶'} Ako zdieľať kalendár?
                            </button>

                            {showCalendarHelp && (
                                <div className="help-content">
                                    <ol>
                                        <li>Otvorte <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">Google Calendar</a></li>
                                        <li>Kliknite na ⋮ pri kalendári → <strong>Nastavenia a zdieľanie</strong></li>
                                        <li>V sekcii <strong>"Zdieľať s konkrétnymi ľuďmi"</strong> kliknite na <strong>"Pridať ľudí"</strong></li>
                                        <li>Zadajte email: <code className="service-account-email">{SERVICE_ACCOUNT_EMAIL}</code>
                                            <button
                                                className="copy-btn"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(SERVICE_ACCOUNT_EMAIL);
                                                    alert('Email skopírovaný!');
                                                }}
                                                title="Kopírovať"
                                            >
                                                📋
                                            </button>
                                        </li>
                                        <li>Vyberte oprávnenie: <strong>"Zobraziť všetky podrobnosti udalostí"</strong></li>
                                        <li>Kliknite na <strong>"Odoslať"</strong></li>
                                    </ol>
                                </div>
                            )}
                        </div>

                        <div className="modal-actions">
                            <button
                                onClick={handleTestCalendar}
                                disabled={!newCalendarEmail || testingCalendar || submitting}
                                className="test-btn"
                            >
                                {testingCalendar ? 'Testujem...' : '🔍 Otestovať pripojenie'}
                            </button>
                            <button
                                onClick={handleAddCalendar}
                                disabled={!calendarTestResult?.success || submitting}
                                className="confirm-btn"
                            >
                                {submitting ? 'Pridávam...' : 'Pridať kalendár'}
                            </button>
                            <button
                                onClick={handleCloseCalendarModal}
                                disabled={submitting}
                                className="cancel-btn"
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal pre pridanie dostupnosti */}
            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Pridať čas pre {selectedDay}</h3>
                        <div className="form-group">
                            <label>Začiatok (HH:MM):</label>
                            <input
                                type="time"
                                value={newBlock.start_time}
                                onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Koniec (HH:MM):</label>
                            <input
                                type="time"
                                value={newBlock.end_time}
                                onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })}
                            />
                        </div>
                        {newBlock.start_time && newBlock.end_time && newBlock.start_time >= newBlock.end_time && (
                            <div className="validation-error">
                                Čas začiatku musí byť pred časom konca
                            </div>
                        )}
                        <div className="modal-actions">
                            <button
                                onClick={handleAddBlock}
                                disabled={submitting || (newBlock.start_time && newBlock.end_time && newBlock.start_time >= newBlock.end_time)}
                                className="confirm-btn"
                            >
                                {submitting ? 'Pridávam...' : 'Pridať čas'}
                            </button>
                            <button
                                onClick={() => setShowAddModal(false)}
                                disabled={submitting}
                                className="cancel-btn"
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal pre jednorazovú nedostupnosť */}
            {showUnavailabilityModal && (
                <div className="modal-overlay">
                    <div className="modal wide-modal">
                        <h3>{editingUnavailability ? 'Upraviť nedostupnosť' : 'Pridať jednorazovú nedostupnosť'}</h3>

                        <div className="date-range-picker">
                            {/* Začiatok */}
                            <div className="date-section">
                                <h4>Začiatok nedostupnosti</h4>
                                <div className="date-picker-group">
                                    <div className="form-group compact">
                                        <label>Deň</label>
                                        {renderDaySelect(
                                            unavailabilityData.startDate.day,
                                            updateStartDate,
                                            unavailabilityData.startDate.month,
                                            unavailabilityData.startDate.year,
                                            'start'
                                        )}
                                    </div>
                                    <div className="form-group compact">
                                        <label>Mesiac</label>
                                        <select
                                            value={unavailabilityData.startDate.month}
                                            onChange={(e) => updateStartDate('month', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">MM</option>
                                            {generateMonths().map(month => (
                                                <option key={`start-${month}`} value={month}>{month}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group compact">
                                        <label>Rok</label>
                                        <select
                                            value={unavailabilityData.startDate.year}
                                            onChange={(e) => updateStartDate('year', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">RRRR</option>
                                            {generateYears().map(year => (
                                                <option key={`start-${year}`} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group compact">
                                        <label>Čas</label>
                                        <input
                                            type="time"
                                            value={unavailabilityData.startDate.time}
                                            onChange={(e) => updateStartDate('time', e.target.value)}
                                            className="time-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Koniec */}
                            <div className="date-section">
                                <h4>Koniec nedostupnosti</h4>
                                <div className="date-picker-group">
                                    <div className="form-group compact">
                                        <label>Deň</label>
                                        {renderDaySelect(
                                            unavailabilityData.endDate.day,
                                            updateEndDate,
                                            unavailabilityData.endDate.month,
                                            unavailabilityData.endDate.year,
                                            'end'
                                        )}
                                    </div>
                                    <div className="form-group compact">
                                        <label>Mesiac</label>
                                        <select
                                            value={unavailabilityData.endDate.month}
                                            onChange={(e) => updateEndDate('month', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">MM</option>
                                            {generateMonths().map(month => (
                                                <option key={`end-${month}`} value={month}>{month}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group compact">
                                        <label>Rok</label>
                                        <select
                                            value={unavailabilityData.endDate.year}
                                            onChange={(e) => updateEndDate('year', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">RRRR</option>
                                            {generateYears().map(year => (
                                                <option key={`end-${year}`} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group compact">
                                        <label>Čas</label>
                                        <input
                                            type="time"
                                            value={unavailabilityData.endDate.time}
                                            onChange={(e) => updateEndDate('time', e.target.value)}
                                            className="time-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {validationErrors.unavailabilityRange && (
                            <div className="validation-error">
                                {validationErrors.unavailabilityRange}
                            </div>
                        )}

                        <div className="selected-date-preview">
                            <strong>Vybrané obdobie:</strong>
                            {unavailabilityData.startDate.day && unavailabilityData.startDate.month && unavailabilityData.startDate.year ? (
                                <span>
                                    {unavailabilityData.startDate.day}.{unavailabilityData.startDate.month}.{unavailabilityData.startDate.year} {unavailabilityData.startDate.time}
                                    {' → '}
                                    {unavailabilityData.endDate.day && unavailabilityData.endDate.month && unavailabilityData.endDate.year
                                        ? `${unavailabilityData.endDate.day}.${unavailabilityData.endDate.month}.${unavailabilityData.endDate.year} ${unavailabilityData.endDate.time}`
                                        : '?'
                                    }
                                </span>
                            ) : (
                                <span>Nie je vybrané obdobie</span>
                            )}
                        </div>

                        <div className="modal-actions">
                            <button
                                onClick={handleSaveUnavailabilityBlock}
                                disabled={submitting || validationErrors.unavailabilityRange}
                                className="confirm-btn"
                            >
                                {submitting ? 'Ukladám...' : (editingUnavailability ? 'Uložiť zmeny' : 'Pridať nedostupnosť')}
                            </button>
                            <button
                                onClick={handleCloseUnavailabilityModal}
                                disabled={submitting}
                                className="cancel-btn"
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal pre opakovanú nedostupnosť */}
            {showRecurringModal && (
                <div className="modal-overlay">
                    <div className="modal wide-modal">
                        <h3>{editingRecurring ? 'Upraviť opakovanú nedostupnosť' : 'Pridať opakovanú nedostupnosť'}</h3>

                        <div className="recurring-form">
                            {/* Typ opakovania */}
                            <div className="form-group">
                                <label>Typ opakovania</label>
                                <select
                                    value={recurringData.recurrence_type}
                                    onChange={(e) => setRecurringData(prev => ({ ...prev, recurrence_type: e.target.value }))}
                                    className="date-select full-width"
                                >
                                    {recurrenceTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Deň v týždni */}
                            <div className="form-group">
                                <label>Deň v týždni</label>
                                <select
                                    value={recurringData.day_of_week}
                                    onChange={(e) => setRecurringData(prev => ({ ...prev, day_of_week: e.target.value }))}
                                    className="date-select full-width"
                                >
                                    {daysOfWeek.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Čas */}
                            <div className="time-picker-row">
                                <div className="form-group">
                                    <label>Čas od</label>
                                    <input
                                        type="time"
                                        value={recurringData.start_time}
                                        onChange={(e) => setRecurringData(prev => ({ ...prev, start_time: e.target.value }))}
                                        className="time-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Čas do</label>
                                    <input
                                        type="time"
                                        value={recurringData.end_time}
                                        onChange={(e) => setRecurringData(prev => ({ ...prev, end_time: e.target.value }))}
                                        className="time-input"
                                    />
                                </div>
                            </div>

                            {recurringData.start_time && recurringData.end_time && recurringData.start_time >= recurringData.end_time && (
                                <div className="validation-error">
                                    Čas začiatku musí byť pred časom konca
                                </div>
                            )}

                            {/* Platnosť od (voliteľné) */}
                            <div className="date-section">
                                <h4>Platné od (voliteľné)</h4>
                                <div className="date-picker-group">
                                    <div className="form-group compact">
                                        <label>Deň</label>
                                        {renderDaySelect(
                                            recurringData.start_from_date.day,
                                            updateRecurringStartFromDate,
                                            recurringData.start_from_date.month,
                                            recurringData.start_from_date.year,
                                            'rec-from'
                                        )}
                                    </div>
                                    <div className="form-group compact">
                                        <label>Mesiac</label>
                                        <select
                                            value={recurringData.start_from_date.month}
                                            onChange={(e) => updateRecurringStartFromDate('month', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">MM</option>
                                            {generateMonths().map(month => (
                                                <option key={`from-${month}`} value={month}>{month}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group compact">
                                        <label>Rok</label>
                                        <select
                                            value={recurringData.start_from_date.year}
                                            onChange={(e) => updateRecurringStartFromDate('year', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">RRRR</option>
                                            {generateYears().map(year => (
                                                <option key={`from-${year}`} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Platnosť do (povinné) */}
                            <div className="date-section">
                                <h4>Platné do *</h4>
                                <div className="date-picker-group">
                                    <div className="form-group compact">
                                        <label>Deň</label>
                                        {renderDaySelect(
                                            recurringData.end_date.day,
                                            updateRecurringEndDate,
                                            recurringData.end_date.month,
                                            recurringData.end_date.year,
                                            'rec-to'
                                        )}
                                    </div>
                                    <div className="form-group compact">
                                        <label>Mesiac</label>
                                        <select
                                            value={recurringData.end_date.month}
                                            onChange={(e) => updateRecurringEndDate('month', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">MM</option>
                                            {generateMonths().map(month => (
                                                <option key={`to-${month}`} value={month}>{month}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group compact">
                                        <label>Rok</label>
                                        <select
                                            value={recurringData.end_date.year}
                                            onChange={(e) => updateRecurringEndDate('year', e.target.value)}
                                            className="date-select"
                                        >
                                            <option value="">RRRR</option>
                                            {generateYears().map(year => (
                                                <option key={`to-${year}`} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {validationErrors.recurringRange && (
                            <div className="validation-error">
                                {validationErrors.recurringRange}
                            </div>
                        )}

                        <div className="selected-date-preview">
                            <strong>Zhrnutie:</strong>
                            <span>
                                {getRecurrenceLabel(recurringData.recurrence_type)} - {recurringData.day_of_week}
                                {recurringData.start_time && recurringData.end_time && ` ${recurringData.start_time} - ${recurringData.end_time}`}
                                {recurringData.end_date.day && recurringData.end_date.month && recurringData.end_date.year &&
                                    ` (do ${recurringData.end_date.day}.${recurringData.end_date.month}.${recurringData.end_date.year})`
                                }
                            </span>
                        </div>

                        <div className="modal-actions">
                            <button
                                onClick={handleSaveRecurringUnavailability}
                                disabled={
                                    submitting ||
                                    validationErrors.recurringRange ||
                                    (recurringData.start_time && recurringData.end_time && recurringData.start_time >= recurringData.end_time)
                                }
                                className="confirm-btn"
                            >
                                {submitting ? 'Ukladám...' : (editingRecurring ? 'Uložiť zmeny' : 'Pridať')}
                            </button>
                            <button
                                onClick={handleCloseRecurringModal}
                                disabled={submitting}
                                className="cancel-btn"
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal pre nastavenia */}
            {showSettingsModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Upraviť: {getSettingTitle(editingSetting?.typ)}</h3>
                        <div className="form-group">
                            <label>Nová hodnota:</label>

                            {/* Pre "Medzera" použijeme select s preddefinovanými hodnotami */}
                            {editingSetting?.typ === 'Medzera' ? (
                                <select
                                    value={newSettingValue}
                                    onChange={(e) => setNewSettingValue(e.target.value)}
                                    className="date-select full-width"
                                >
                                    {getGapOptions().map(value => (
                                        <option key={value} value={value.toString()}>
                                            {value} minút
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="number"
                                    value={newSettingValue}
                                    onChange={(e) => setNewSettingValue(e.target.value)}
                                    min="1"
                                    placeholder={editingSetting?.typ === 'Najskôr' ? 'Počet hodín' : 'Počet dní'}
                                />
                            )}

                            <small className="setting-help-text">
                                {getSettingDescription(editingSetting?.typ)}
                            </small>
                        </div>
                        <div className="modal-actions">
                            <button
                                onClick={handleSaveSetting}
                                disabled={submitting || !newSettingValue}
                                className="confirm-btn"
                            >
                                {submitting ? 'Ukladám...' : 'Uložiť'}
                            </button>
                            <button
                                onClick={handleCloseSettingsModal}
                                disabled={submitting}
                                className="cancel-btn"
                            >
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AvailabilityManagement;